import { useCallback, useEffect, useRef } from 'react'

import { useWidgetBreakpointSettings } from '@/hooks/useWidgetBreakpointSettings'
import { useWidgetStore } from '@/stores/useWidgetStore'
import { resolveIconPadding } from '@/utils/widget-utils'

const DRAG_THRESHOLD_PX = 5
const MIN_VISIBLE_PX = 40

type OverlayPos = { x: number; y: number }

function clampOverlayPosition(
  position: OverlayPos,
  anchor: string,
  padding: { top: number; right: number; bottom: number; left: number },
  size: { width: number; height: number },
  viewportW = window.innerWidth,
  viewportH = window.innerHeight
): OverlayPos {
  const isRight = anchor.includes('right')
  const isTop = anchor.startsWith('top')

  // Overlay root uses fixed corner insets + transform: translate(x, y)
  const left = isRight ? viewportW - padding.right - size.width + position.x : padding.left + position.x
  const top = isTop ? padding.top + position.y : viewportH - padding.bottom - size.height + position.y

  const clampedLeft = Math.min(Math.max(left, MIN_VISIBLE_PX - size.width), viewportW - MIN_VISIBLE_PX)
  const clampedTop = Math.min(Math.max(top, MIN_VISIBLE_PX - size.height), viewportH - MIN_VISIBLE_PX)

  return {
    x: isRight ? clampedLeft - (viewportW - padding.right - size.width) : clampedLeft - padding.left,
    y: isTop ? clampedTop - padding.top : clampedTop - (viewportH - padding.bottom - size.height),
  }
}

/**
 * Drag the floating launcher overlay (WidgetOverlay / WidgetIcon).
 * Distinguishes click vs drag via a small movement threshold so open-on-click still works.
 */
export function useOverlayDrag() {
  const overlayPosition = useWidgetStore((s) => s.overlayPosition)
  const setOverlayPosition = useWidgetStore((s) => s.setOverlayPosition)
  const isOverlayDragging = useWidgetStore((s) => s.isOverlayDragging)
  const setIsOverlayDragging = useWidgetStore((s) => s.setIsOverlayDragging)
  const { iconSettings } = useWidgetBreakpointSettings()

  const dragStartRef = useRef<{
    x: number
    y: number
    startPos: OverlayPos
    pointerId: number
    moved: boolean
  } | null>(null)
  const suppressClickRef = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return

      const target = e.currentTarget as HTMLElement
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        startPos: { ...overlayPosition },
        pointerId: e.pointerId,
        moved: false,
      }
      suppressClickRef.current = false

      try {
        target.setPointerCapture(e.pointerId)
      } catch {
        // window listeners still cover mouse
      }
    },
    [overlayPosition]
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const start = dragStartRef.current
      if (!start || e.pointerId !== start.pointerId) return

      const deltaX = e.clientX - start.x
      const deltaY = e.clientY - start.y

      if (!start.moved) {
        if (Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) return
        start.moved = true
        suppressClickRef.current = true
        setIsOverlayDragging(true)
      }

      e.preventDefault()

      const anchor = iconSettings?.position ?? 'bottom-left'
      const padding = resolveIconPadding(iconSettings?.padding)
      const size = {
        width: iconSettings?.width ?? 48,
        height: iconSettings?.height ?? 48,
      }

      setOverlayPosition(
        clampOverlayPosition(
          {
            x: start.startPos.x + deltaX,
            y: start.startPos.y + deltaY,
          },
          anchor,
          padding,
          size
        )
      )
    },
    [iconSettings, setIsOverlayDragging, setOverlayPosition]
  )

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const start = dragStartRef.current
      if (!start || e.pointerId !== start.pointerId) return

      dragStartRef.current = null
      if (start.moved) setIsOverlayDragging(false)
    },
    [setIsOverlayDragging]
  )

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [handlePointerMove, handlePointerUp])

  const shouldSuppressClick = useCallback(() => {
    if (!suppressClickRef.current) return false
    suppressClickRef.current = false
    return true
  }, [])

  return {
    handlePointerDown,
    shouldSuppressClick,
    isOverlayDragging,
    overlayPosition,
    dragStyle: {
      cursor: isOverlayDragging ? ('grabbing' as const) : ('grab' as const),
      touchAction: 'none' as const,
      userSelect: 'none' as const,
    },
  }
}
